import { Dispatch, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import { PsRadioTuner, PsButtonTuner } from "stencilSetup";
import { LumpSumEntry } from './LumpSumEntry';
import {
  validateLumpSumTotal,
  getValidationMessages,
  regularEmployeeContributionAmount
} from 'enrollment/components/ContributionsCard/utils/utils';
import {
  hsaContributionsIntial,
  updateScheduledContribution,
  updateResetAllEmployeeChanges
} from "../../../../services";
import { ContributionAdjustedBanner } from './HSAContributionStyled';

interface LumpSumContributionProps {
  validationMessages: any;
  setValidationMessages: Dispatch<any>;
  onResetChanges: (reset:boolean) => void;
}

export function LumpSumContribution({validationMessages, setValidationMessages, onResetChanges}: LumpSumContributionProps) {
  const { t } = useTranslation('pages.financial');
  const dispatch = useDispatch();
  const hsaContribution = useSelector(hsaContributionsIntial);
  const paycheckDates = hsaContribution.employee_contribution_details.remaining_paycheck_dates;
  const maxAmount = hsaContribution.employee_contribution_details.calculated_subscriber_max;
  const submitContributionSchedule = hsaContribution?.disable_hsa_form;
  const totalLumpSumContribution = Number(regularEmployeeContributionAmount(hsaContribution, 'scheduled_subscriber_lump_sum_contribution'));
  const totalRegularContribution = Number(regularEmployeeContributionAmount(hsaContribution, 'scheduled_subscriber_contribution'));
  const remainingLumpSumAmount = Number(maxAmount) - totalRegularContribution;
  const maxContributionReached = totalRegularContribution >= Number(maxAmount) && totalLumpSumContribution <= 0;

  const getAvailableDates = (entryId: number) => {
    const selectedDates = lumpSumEntries
      .filter(entry => entry.id !== entryId)
      .map(entry => entry.date);
  
    return paycheckDates.filter(date => !selectedDates.includes(date));
  };

  const initializeLumpSumEntries = () => {
    if (hsaContribution.scheduled_contribution) {
      return hsaContribution.scheduled_contribution
        .filter(contribution => parseFloat(contribution?.scheduled_subscriber_lump_sum_contribution?.toString() || '0') > 0)
        .map((contribution, index) => ({
          id: index + 1,
          amount: contribution?.scheduled_subscriber_lump_sum_contribution?.toString() || '',
          date: contribution.paycheck
        }));
    } else {
      return [];
    }
  };

  const initializeLumpSum = () => {
    let initialLumpsumState = '';
    if (Number(hsaContribution?.employee_contribution_details?.total_ee_contribution_amount) > 0){
      initialLumpsumState = Number(hsaContribution?.employee_contribution_details?.total_ee_lump_sum_contribution_amount) > 0 ? 'yes' : 'no';
    }
    return initialLumpsumState;
  };

  const checkForValidations = () => {
    const availablePayCheckDates =  paycheckDates.filter(date => !lumpSumEntries.some(entry => entry.date === date));
    return availablePayCheckDates.length <= 0;
  };

  const [lumpSumEntries, setLumpSumEntries] = useState([]);
  const [lumpSumContribution, setLumpSumContribution] = useState('');
  const isTotalInValid = validateLumpSumTotal(lumpSumEntries, remainingLumpSumAmount);
  const isAddButtonDisabled = lumpSumEntries.some(entry => !entry.amount || !entry.date) || lumpSumEntries.length >= 12 || isTotalInValid || checkForValidations();

  useMemo(() => {
    if(hsaContribution.employee_contribution_details?.total_ee_lump_sum_contribution_amount && hsaContribution.scheduled_contribution){
      const initialLumpSum = initializeLumpSum();
      setLumpSumContribution(initialLumpSum);
      const initialLumpSumEntries = initializeLumpSumEntries();
      setLumpSumEntries(initialLumpSumEntries);
    };
  }, [hsaContribution.employee_contribution_details?.total_ee_lump_sum_contribution_amount]);


  useEffect(() => {
    if(hsaContribution.disable_hsa_form) {
      const totalLumpSumAmount = Number(regularEmployeeContributionAmount(hsaContribution, 'scheduled_subscriber_lump_sum_contribution'));
      setLumpSumContribution(Number(totalLumpSumAmount) > 0 ? 'yes' : 'no');
    }
  },[hsaContribution.scheduled_contribution]);

  useEffect(() => {
    if(hsaContribution.reset_all_employee_changes) {
      setLumpSumContribution('');
      setLumpSumEntries([]);
      dispatch(updateResetAllEmployeeChanges(false));
    }
  },[hsaContribution]);

  const handleAddLumpSum = () => {
    const maxId = lumpSumEntries.reduce((max, entry) => Math.max(max, entry.id), 0);
    const newEntry = { id: maxId + 1, amount: '', date: '' };
    setLumpSumEntries([...lumpSumEntries, newEntry]);
  };

  const handleDeleteLumpSum = (id: number) => {
    const entryToDelete = lumpSumEntries.find(entry => entry.id === id);
    const filteredEntries = lumpSumEntries.filter(entry => entry.id !== id);
    setLumpSumEntries(filteredEntries);

    if (entryToDelete) {
      updateScheduleContributions(filteredEntries, entryToDelete);
      const updatedValidationMessages = { ...validationMessages };
      delete updatedValidationMessages[entryToDelete.id];
      setValidationMessages(updatedValidationMessages);
    }
  };

  const updateScheduleContributions = (updatedEntries: Array<any>, entryToDelete?: any) => {
    const updatedScheduledContribution = hsaContribution?.scheduled_contribution?.map(contribution => {
      if (entryToDelete && contribution.paycheck === entryToDelete?.date) {
        return {
          ...contribution,
          scheduled_subscriber_lump_sum_contribution: ''
        };
      }
      const matchingEntry = updatedEntries.find(entry => entry.date === contribution.paycheck);
      if (matchingEntry) {
        return {
          ...contribution,
          scheduled_subscriber_lump_sum_contribution: Number(matchingEntry.amount).toFixed(2)
        };
      }
      return {
        ...contribution,
        scheduled_subscriber_lump_sum_contribution: ''
      };;
    });

    dispatch(updateScheduledContribution({ scheduled_contribution: updatedScheduledContribution || [], disable_hsa_form: false }));
  };

  const handleToggleSwitch = (value: string) => {
    setLumpSumContribution(value);
    if (value === 'yes') {
      setLumpSumEntries((hsaContribution.scheduled_contribution && hsaContribution.scheduled_contribution.some(contribution => parseFloat(contribution?.scheduled_subscriber_lump_sum_contribution?.toString() || '0') > 0.0)) ? initializeLumpSumEntries() : [{ id: 1, amount: '', date: '' }]);
    } else {
      //Reset and Update scheduled contributions by clearing lump sum values
      setLumpSumEntries([]);
      setValidationMessages({});
      const updatedContributions = hsaContribution?.scheduled_contribution?.map(item => ({
        ...item,
        scheduled_subscriber_lump_sum_contribution: ''
      }));
      dispatch(updateScheduledContribution({ scheduled_contribution: updatedContributions || [], disable_hsa_form: false }));
    }
  };


  useEffect(() => {
    if(maxContributionReached){
      handleToggleSwitch('no');
    };
  }, [hsaContribution?.employee_contribution_details?.total_ee_contribution_amount]);


  const handleInputChange = (id: number, field: string, target: any) => {
    const updatedEntries = lumpSumEntries.map(entry => {
      if (entry.id === id) {
        return { ...entry, [field]: target.value };
      }
      return entry;
    });
    setValidationMessages(getValidationMessages(id, updatedEntries, maxAmount, remainingLumpSumAmount));
  };

  const handleChangeOnBlur = (id: number, field: string, target: any) => {
    const updatedEntries = lumpSumEntries.map(entry => {
      if (entry.id === id) {
        return { ...entry, [field]: target.value };
      }
      return entry;
    });
    setLumpSumEntries(updatedEntries);
    setValidationMessages(getValidationMessages(id, updatedEntries, maxAmount, remainingLumpSumAmount));
    updateScheduleContributions(updatedEntries);
  };

  const isScreenSmall = window.innerWidth < 922;
  
  return (
    <div className={isScreenSmall ? "pb-2" : "pt-4 pb-2"}>
      <div className={`semiBold pt-3 ${submitContributionSchedule ? 'disabled' : ''}`}>{t('employeeContribution.lumpSumContributionText')}</div>
        <div className="d-flex lump-sum-text">
          <PsRadioTuner className={`${submitContributionSchedule ? 'disabled' : ''}`} data-testid="lump_sum_yes" inputId="lump_sum_yes" name="lump_sum_contribution" label="Yes" checked={lumpSumContribution === 'yes'} value="yes" disabled={maxContributionReached} onPsChange={() => handleToggleSwitch('yes')} />
          <PsRadioTuner className={`${submitContributionSchedule ? 'disabled' : ''}`} data-testid="lump_sum_no" inputId="lump_sum_no" name="lump_sum_contribution" label="No" checked={lumpSumContribution === 'no' || maxContributionReached} value="no" disabled={maxContributionReached} onPsChange={() => handleToggleSwitch('no')}/>
        </div>
        {submitContributionSchedule && <>
          <ContributionAdjustedBanner>
              <div data-testid="contribution-adjusted" className="semiBold contribution-title">{t('employeeContribution.contributionAdjustedTitle')}</div>
              <div data-testid="contribution-adjusted-desc">{t('employeeContribution.contributionAdjustedDescription')}</div>
          </ContributionAdjustedBanner>
          <div className="mx-4 mt-4 mb-2">
            <PsButtonTuner
              data-testid="reset-all-btn"
              id="reset-all-btn"
              buttonId="reset-all-btn"
              level="tertiary"
              link={true}
              label={t('employeeContribution.resetAll')}
              onClick={() => {
                dispatch(updateResetAllEmployeeChanges(true));
                onResetChanges(true);}}
            />
          </div>
        </>}
      {!submitContributionSchedule && lumpSumContribution!=='' && lumpSumEntries.length > 0 && (
        <div>
          {lumpSumEntries.map((entry) => (
            <LumpSumEntry
              key={entry.id}
              entry={entry}
              onDelete={handleDeleteLumpSum}
              onInputChange={handleInputChange}
              onBlur={handleChangeOnBlur}
              validationMessage={validationMessages[entry.id]}
              payCheckDates={paycheckDates}
              availableDates={getAvailableDates(entry.id)}
            />
          ))}
          <div className={isScreenSmall?"pt-4":"py-3_5"}>
            <PsButtonTuner
              data-testid='add-another-lump-sum-button'
              id="add-another-lump-sum-button"
              buttonId="add-another-lump-sum-button"
              level="tertiary"
              label={t('employeeContribution.addAnotherLumpSum')}
              onClick={() => {
                if (!isAddButtonDisabled) {
                  handleAddLumpSum();
                }
              }}
              disabled= {isAddButtonDisabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}
